<template>
  <div
    class="form-control password-input"
    :class="{ 'in-focus': focus, 'is-hidden': hidden, 'is-disabled': disabled }"
  >
    <input
      ref="inputEl"
      :autocomplete="autocomplete"
      :name="name"
      :readonly="preventAutofill && !focus"
      :type="hidden ? 'password' : 'text'"
      :value="displayValue"
      class="form-control radius-left"
      :disabled="disabled"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
    />
    <Tooltip
      v-if="showEye"
      :text="help"
    >
      <div
        class="password-toggle"
        @mousedown.prevent
        @pointerdown.prevent
        @click="toggleHidden"
      >
        <i
          class="bi"
          :class="{ 'bi-eye': hidden, 'bi-eye-slash': !hidden }"
        ></i>
      </div>
    </Tooltip>
  </div>
</template>
<script setup>
import { computed, nextTick, shallowRef, ref } from 'vue'
import Tooltip from '@/components/tooltip.vue'
import { useI18n } from 'vue-i18n'

const PLACEHOLDER_DOTS = '••••••••'

const props = defineProps({
  disabled: { type: Boolean, default: false },
  hasStoredPassword: { type: Boolean, default: false },
  autocomplete: { type: String, default: 'new-password' },
  name: { type: String, default: undefined },
})
const model = defineModel({ default: '' })
const { t } = useI18n()

const inputEl = ref(null)
const focus = shallowRef(false)
const hidden = shallowRef(true)
const help = computed(() => (hidden.value ? t('showPassword') : t('hidePassword')))

const preventAutofill = computed(() => props.autocomplete === 'new-password')
const isStoredMaskMode = computed(() => props.hasStoredPassword && !focus.value)
const displayValue = computed(() => (isStoredMaskMode.value ? PLACEHOLDER_DOTS : String(model.value ?? '')))
const showEye = computed(() => focus.value && String(model.value ?? '').length > 0)

function onInput(e) {
  if (props.disabled) return
  model.value = String(e?.target?.value ?? '')
}

async function onFocus() {
  if (props.disabled) return
  if (preventAutofill.value) inputEl.value?.removeAttribute('readonly')
  focus.value = true
  hidden.value = true
  // Если пароль в БД есть, но в модели пусто — очищаем плейсхолдер-точки при начале ввода.
  if (props.hasStoredPassword && !String(model.value ?? '').length) {
    await nextTick()
    if (inputEl.value) inputEl.value.value = ''
  }
}

function onBlur() {
  focus.value = false
  hidden.value = true
}

function toggleHidden() {
  if (props.disabled) return
  hidden.value = !hidden.value
}

defineExpose({
  getRawValue: () => String(inputEl.value?.value ?? model.value ?? ''),
})
</script>
<style scoped>
.password-toggle {
  height: 38px;
  cursor: pointer;
}
:deep(.password-toggle) {
  display: flex;
  align-items: center;
}
.password-input.is-disabled .password-toggle {
  pointer-events: none;
  opacity: 0.6;
  cursor: default;
}
i {
  padding: 0 1rem;
}
.radius-left {
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
}
</style>

<i18n>
{
  "ru": {
    "showPassword": "Показать пароль",
    "hidePassword": "Скрыть пароль"
  },
  "en": {
    "showPassword": "Show password",
    "hidePassword": "Hide password"
  }
}
</i18n>
